// src/questionnaire/ReservedPoolQuestion.js
import React, { useState } from "react";
import { useTeamContext } from "../TeamContext";
import SliderWithButtons from "../ui/SliderWithButtons";
import { Check } from "lucide-react";
import { getFactorIcon } from "./icon-mapping";

const suggestedAdditionalShareholders = [
    { name: "Employee Stock Pool", tooltip: "Equity allocated for employees/future hires" },
    { name: "Independent Directors", tooltip: "Equity for board directors" },
    { name: "Third Party Advisors", tooltip: "Equity for advisors" },
    { name: "Potential Investors", tooltip: "Equity reserved for investors" },
];

function ReservedPoolQuestion({ workspace }) {
    const { updateWorkspaceData } = useTeamContext();
    const pools = workspace.part1Answers.reservedPools || [];
    const [newPoolName, setNewPoolName] = useState("");

    const addPool = (name) => {
        const existingIndex = pools.findIndex(pool => pool.name === name);
        if (existingIndex !== -1) {
            removePool(existingIndex);
            return;
        }

        const newItem = { name, weight: 0 };
        const newP1 = {
            ...workspace.part1Answers,
            reservedPools: [...pools, newItem],
        };
        updateWorkspaceData(workspace.id, { ...workspace, part1Answers: newP1 });
    };

    const removePool = (idx) => {
        const updated = [...pools];
        updated.splice(idx, 1);
        const newP1 = { ...workspace.part1Answers, reservedPools: updated };
        updateWorkspaceData(workspace.id, { ...workspace, part1Answers: newP1 });
    };

    const updateSlider = (idx, val) => {
        const updated = [...pools];
        updated[idx].weight = val;
        const newP1 = { ...workspace.part1Answers, reservedPools: updated };
        updateWorkspaceData(workspace.id, { ...workspace, part1Answers: newP1 });
    };

    const scrollContainerStyle = {
        maxHeight: "400px",
        overflowY: "auto",
        padding: "8px",
        border: "1px solid #ECECEC",
        borderRadius: "8px",
        margin: "16px 0"
    };

    const getCardStyle = (isSelected) => ({
        display: "flex",
        alignItems: "center",
        padding: "16px",
        marginBottom: "8px",
        border: `1px solid ${isSelected ? "#ea7a0a" : "#ECECEC"}`,
        borderRadius: "8px",
        backgroundColor: isSelected ? "#f0f7ff" : "#fff",
        cursor: "pointer",
        transition: "all 0.2s ease",
        transform: isSelected ? "scale(1.005)" : "none",
        color: "#28435a"
    });

    return (
        <div style={{ color: "#28435a" }}>
            <div style={scrollContainerStyle}>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {suggestedAdditionalShareholders.map((s) => {
                        const isSelected = pools.some(pool => pool.name === s.name);
                        return (
                            <div
                                key={s.name}
                                style={getCardStyle(isSelected)}
                                onClick={() => addPool(s.name)}
                                data-tooltip-id="reserved-pool-tooltip"
                                data-tooltip-content={s.tooltip}
                            >
                                <div style={{
                                    width: "40px",
                                    height: "40px",
                                    borderRadius: "50%",
                                    backgroundColor: isSelected ? "#ea7a0a20" : "#F3F4F6",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    marginRight: "16px"
                                }}>
                                    {getFactorIcon(s.name)}
                                </div>
                                <div style={{ flexGrow: 1 }}>
                                    <h4 style={{ margin: 0 }}>{s.name}</h4>
                                    <p style={{ margin: "4px 0 0", fontSize: "0.875rem", color: "#6B7280" }}>
                                        {s.tooltip}
                                    </p>
                                </div>
                                <div style={{
                                    width: "24px",
                                    height: "24px",
                                    borderRadius: "50%",
                                    border: `2px solid ${isSelected ? "#ea7a0a" : "#D1D5DB"}`,
                                    backgroundColor: isSelected ? "#ea7a0a" : "transparent",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }}>
                                    {isSelected && <Check size={16} color="#fff" />}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div style={{ marginBottom: "12px" }}>
                <input
                    type="text"
                    placeholder="Custom Equity Pool"
                    value={newPoolName}
                    onChange={(e) => setNewPoolName(e.target.value)}
                    style={{
                        marginRight: "8px",
                        padding: "6px",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                        color: "#28435a"
                    }}
                />
                <button
                    style={{
                        backgroundColor: "#f7931e",
                        color: "#fff",
                        border: "2px solid #f7931e",
                        padding: "8px 16px",
                        borderRadius: "4px",
                        cursor: "pointer",
                        transition: "background .2s, color .2s, border .2s"
                    }}
                    onClick={() => {
                        if (newPoolName.trim()) {
                            addPool(newPoolName.trim());
                            setNewPoolName("");
                        }
                    }}
                >
                    + Add
                </button>
            </div>

            {pools.length === 0 && <p>No equity pools added yet.</p>}

            {pools.map((pool, idx) => (
                <div key={idx} style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
                    <strong style={{ width: "200px" }}>{pool.name}</strong>
                    <SliderWithButtons
                        value={pool.weight}
                        min={0}
                        max={100}
                        step={1}
                        onChange={(val) => updateSlider(idx, val)}
                    />
                    <button
                        onClick={() => removePool(idx)}
                        style={{
                            marginLeft: "8px",
                            background: "none",
                            border: "none",
                            color: "#df1c1c",
                            fontSize: "20px",
                            cursor: "pointer"
                        }}
                    >
                        &times;
                    </button>
                </div>
            ))}
        </div>
    );
}

export default ReservedPoolQuestion;
